import { useEffect, useMemo, useRef, useState } from "react";
import ThemeToggle from "../features/settings/Theme";
import { CloseBtn } from "../components/ui";
import RightToLeftLayout from "../features/settings/RightToLeft";
import { useAlarmSoundContext } from "../contexts/AlarmSoundContext";
import { useSidebarVisibility } from "../contexts/SidebarVisibilityContext";
import { useTreeRefresh } from "../contexts/TreeRefreshContext";
import { useAuthContext } from "../contexts/useAuthContext";
import {
    buildUserSettingsPayload,
    fetchUserSettingsFromApi,
    loadUserSettings,
    saveUserSettings,
    saveUserSettingsToApi,
    themeModeToIsDark,
    toNumericGroupVisibilityMap,
} from "../services/settings/userSettings.service";

interface SettingsProps {
    isOpen: boolean;
    onClose: () => void;
}

const SETTINGS_SAVE_DEBOUNCE_MS = 500;

const Settings: React.FC<SettingsProps> = ({ isOpen, onClose }) => {
    const { isMuted, toggleMute, setMuted } = useAlarmSoundContext();
    const { admin, csh, alarm, help, addedCsh, setGroupVisibility, cshGroupVisibility, setCshGroupVisibility, setAddedCshVisibility } = useSidebarVisibility();
    const { isAdmin, isSupport, userProfile } = useAuthContext();
    const showAdminSetting = isAdmin() && !isSupport();
    const [isDarkTheme, setIsDarkTheme] = useState(false);
    const hasHydratedSettings = useRef(false);
    const hydratedForUserId = useRef<string | null>(null);

    const settingsUserId = useMemo(() => userProfile?.sub || userProfile?.email || userProfile?.preferred_username || 'anonymous', [userProfile]);
    const hasResolvedUserId = settingsUserId !== 'anonymous';

    const [groups, setGroups] = useState<{ groupId: number; groupName: string; unacknowledgedCount: number }[]>([]);

    const { treeNodes, cshRefreshKey } = useTreeRefresh();
    const disableOtherSidebarSettings = alarm;

    const enforceAlarmOnlySidebar = () => {
        if (admin) {setGroupVisibility('admin', false);}
        if (csh) {setGroupVisibility('csh', false);}
        if (help) {setGroupVisibility('help', false);}

        const groupIdsToDisable = new Set<number>();
        Object.keys(cshGroupVisibility || {}).forEach(groupId => {
            const id = Number(groupId);
            if (Number.isFinite(id)) {
                groupIdsToDisable.add(id);
            }
        });
        groups.forEach(g => {
            if (Number.isFinite(g.groupId)) {
                groupIdsToDisable.add(g.groupId);
            }
        });

        groupIdsToDisable.forEach(id => {
            if (cshGroupVisibility?.[id] !== false) {
                setCshGroupVisibility(id, false);
            }
        });
    };

    const handleAlarmVisibilityChange = (checked: boolean) => {
        setGroupVisibility('alarm', checked);

        if (checked) {
            enforceAlarmOnlySidebar();
        }
    };

    useEffect(() => {
        if (!alarm) {return;}
        enforceAlarmOnlySidebar();
    }, [alarm, admin, csh, help, cshGroupVisibility, groups]);

    useEffect(() => {
        if (!hasResolvedUserId) {return undefined;}
        if (hydratedForUserId.current === settingsUserId) {
            hasHydratedSettings.current = true;
            return undefined;
        }
        let cancelled = false;
        const hydrateSettings = async () => {
            const saved = (await fetchUserSettingsFromApi(settingsUserId)) || loadUserSettings(settingsUserId);
            if (cancelled) {return;}

            if (saved) {
                setIsDarkTheme(themeModeToIsDark(saved.theme.mode));
                setMuted(saved.notifications.alarmSoundMuted);
                setGroupVisibility('admin', saved.sidebar.admin);
                setGroupVisibility('csh', saved.sidebar.csh);
                setGroupVisibility('alarm', saved.sidebar.alarm);
                setGroupVisibility('help', saved.sidebar.help);
                setAddedCshVisibility(saved.sidebar.addedCsh);

                const groupVisibilityMap = toNumericGroupVisibilityMap(saved.sidebar.cshGroupVisibility);
                Object.entries(groupVisibilityMap).forEach(([groupId, visible]) => {
                    const id = Number(groupId);
                    if (Number.isFinite(id)) {
                        setCshGroupVisibility(id, visible);
                    }
                });
            }

            hydratedForUserId.current = settingsUserId;
            hasHydratedSettings.current = true;
        };

        void hydrateSettings();

        return () => {
            cancelled = true;
        };
    }, [hasResolvedUserId, settingsUserId, setAddedCshVisibility, setCshGroupVisibility, setGroupVisibility, setMuted]);

    useEffect(() => {
        if (!hasResolvedUserId || !hasHydratedSettings.current) {return undefined;}

        const payload = buildUserSettingsPayload({
            isDarkTheme,
            isMuted,
            userId: settingsUserId,
            sidebar: {
                admin,
                csh,
                alarm,
                help,
                addedCsh,
                cshGroupVisibility: cshGroupVisibility || {},
            },
        });

        saveUserSettings(payload);

        const timer = window.setTimeout(() => {
            void saveUserSettingsToApi(payload);
        }, SETTINGS_SAVE_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [hasResolvedUserId, settingsUserId, isDarkTheme, isMuted, admin, csh, alarm, help, addedCsh, cshGroupVisibility]);

    useEffect(() => {
        let mounted = true;

        const loadGroups = async () => {
            try {
                // Fetch full summaries and sidebar unack counts, then merge so Settings shows all groups
                const mod = await import('../services/csh/csh.service');
                type GroupSummary = { id: number; name?: string; groupName?: string };

                const s = await mod.fetchCshGroups().catch(() => [] as GroupSummary[]);
                const sidebar = await mod.fetchCshSidebarGroups().catch(() => [] as { groupId: number; groupName?: string; unacknowledgedCount: number }[]);

                let summaries: GroupSummary[] = [];
                if (Array.isArray(s)) { summaries = s; }

                let sidebarArr: { groupId: number; groupName?: string; unacknowledgedCount: number }[] = [];
                if (Array.isArray(sidebar)) { sidebarArr = sidebar; }

                if (!mounted) {return;}

                const unackMap = sidebarArr.reduce((acc: Record<number, number>, b) => {
                    if (b?.groupId != null) {
                        acc[b.groupId] = Number(b.unacknowledgedCount) || 0;
                    }
                    return acc;
                }, {} as Record<number, number>);

                const merged = summaries.map((group: GroupSummary) => ({
                    groupId: group.id,
                    groupName: group.name || group.groupName || `Group ${group.id}`,
                    unacknowledgedCount: unackMap[group.id] ?? 0,
                }));

                // Append sidebar groups that weren't in summaries
                for (const b of sidebarArr) {
                    if (!merged.find(m => m.groupId === b.groupId)) {
                        merged.push({ groupId: b.groupId, groupName: b.groupName || `Group ${b.groupId}`, unacknowledgedCount: Number(b.unacknowledgedCount) || 0 });
                    }
                }

                if (mounted) {setGroups(merged);}
            } catch (e) {
                console.info('Failed to load CSH groups for settings sidebar:', e);
            }
        };

        // initial load
        void loadGroups();

        // Listen for immediate in-page CSH updates
        const handler = () => {
            void loadGroups();
        };
        window.addEventListener('csh-updated', handler);

        return () => { mounted = false; window.removeEventListener('csh-updated', handler); };
    }, [treeNodes, cshRefreshKey]);

    const containerClass = (() => {
        let c = 'settings';
        if (isOpen) { c += ' open'; }
        return c;
    })();

    return (
        <aside className={containerClass}>
            <div className="settings__heading">
                <h4>Settings</h4>
                <CloseBtn onClick={onClose} />
            </div>
            <small>Adjust your settings using the options below.</small>
            <div className="settings__options">
                <h4>Theme</h4>
                <ThemeToggle isDarkTheme={isDarkTheme} onToggle={setIsDarkTheme} />
                <RightToLeftLayout />

               
                <div className="setting-item__col">
                    <h4>Sidebar Settings</h4>
                    
                    {showAdminSetting && (
                        <>
                            <h5>Admin</h5>
                            <label className="setting-label">
                                <input className="setting-checkbox" type="checkbox" checked={admin} disabled={disableOtherSidebarSettings} onChange={e => setGroupVisibility('admin', e.target.checked)} />
                                <span className="setting-text">Show Admin</span>
                            </label>
                        </>
                    )}
                    <h5>CSH Groups</h5>
                    <label className="setting-label">
                        <input className="setting-checkbox" type="checkbox" checked={csh} disabled={disableOtherSidebarSettings} onChange={e => setGroupVisibility('csh', e.target.checked)} /> 
                        <span className="setting-text">Show CSH Groups</span>
                    </label>
                    
                    <h5>Configured CSH Groups</h5>
                    {groups.length > 0 && (
                        <div className="setting-sublist">
                            {groups.map(g => (
                                <label key={g.groupId} className="setting-label setting-label--nested">
                                    <input
                                        className="setting-checkbox"
                                        type="checkbox"
                                        checked={cshGroupVisibility?.[g.groupId] !== false}
                                        disabled={disableOtherSidebarSettings}
                                        onChange={e => setCshGroupVisibility(g.groupId, e.target.checked)}
                                    />
                                    <span className="setting-text">Show {g.groupName}</span>
                                </label>
                            ))}
                        </div>
                    )}
                    <h5>All Alarms</h5>
                    <label className="setting-label">
                        <input className="setting-checkbox" type="checkbox" checked={alarm} onChange={e => handleAlarmVisibilityChange(e.target.checked)} /> 
                        <span className="setting-text">Show All Alarm Groups</span>
                    </label>
                    <h5>Help</h5>
                    <label className="setting-label">
                        <input className="setting-checkbox" type="checkbox" checked={help} disabled={disableOtherSidebarSettings} onChange={e => setGroupVisibility('help', e.target.checked)} /> 
                        <span className="setting-text">Show Help</span>
                    </label>
                </div>
                <div className="setting-item__col">
                    <h5>Alarm Sound Notifications</h5>
                    {(() => {
                        let speakerClass = 'btn-speaker-toggle';
                        if (isMuted) {
                            speakerClass += ' btn-speaker-toggle--muted';
                        } else {
                            speakerClass += ' btn-speaker-toggle--enabled';
                        }
                        return (
                            <button 
                                onClick={toggleMute}
                                className={speakerClass}
                            >
                                {(() => {
                                    if (isMuted) {
                                        return 'Muted';
                                    } else {
                                        return 'Enabled';
                                    }
                                })()}
                            </button>
                        );
                    })()}
                </div>
            </div>
            
        </aside>
    );
}


export default Settings;
